import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';

/** What the user picked. `system` defers to the OS. */
export type ThemePreference = 'light' | 'dark' | 'system';
/** What is actually painted, after resolving `system`. */
export type ResolvedTheme = 'light' | 'dark';

const STORAGE_KEY = 'calendyz-theme';
const DARK_QUERY = '(prefers-color-scheme: dark)';

/** Browser chrome color per theme — mirrors --color-canvas in index.css. */
const THEME_COLOR: Record<ResolvedTheme, string> = {
  light: '#ffffff',
  dark: '#0d0f14',
};

function readStoredPreference(): ThemePreference {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'light' || stored === 'dark' || stored === 'system') return stored;
  } catch {
    // Private browsing / blocked storage — fall through to the default.
  }
  return 'system';
}

function prefersDark(): boolean {
  return window.matchMedia(DARK_QUERY).matches;
}

function resolve(preference: ThemePreference): ResolvedTheme {
  if (preference === 'system') return prefersDark() ? 'dark' : 'light';
  return preference;
}

/** Mirror the resolved theme onto the document. The inline script in
 *  index.html does this once before first paint; this keeps it true after. */
function applyTheme(resolved: ResolvedTheme) {
  const root = document.documentElement;
  root.classList.toggle('dark', resolved === 'dark');
  root.style.colorScheme = resolved;
  document
    .querySelector('meta[name="theme-color"]')
    ?.setAttribute('content', THEME_COLOR[resolved]);
}

interface ThemeContextValue {
  /** The user's choice, including `system`. */
  preference: ThemePreference;
  /** The theme currently painted. Use this for anything that needs a concrete
   *  light/dark value — charts, map tiles, third-party widget props. */
  resolved: ResolvedTheme;
  setPreference: (preference: ThemePreference) => void;
}

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [preference, setPreferenceState] = useState<ThemePreference>(readStoredPreference);
  const [resolved, setResolved] = useState<ResolvedTheme>(() => resolve(readStoredPreference()));

  const setPreference = useCallback((next: ThemePreference) => {
    setPreferenceState(next);
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // Preference just won't survive a reload.
    }
  }, []);

  // Re-resolve whenever the preference changes, and — while on `system` —
  // whenever the OS flips. Same listener/cleanup shape as AdminNav's
  // viewport query.
  useEffect(() => {
    const next = resolve(preference);
    setResolved(next);
    applyTheme(next);

    if (preference !== 'system') return;

    const query = window.matchMedia(DARK_QUERY);
    const onChange = (event: MediaQueryListEvent) => {
      const updated: ResolvedTheme = event.matches ? 'dark' : 'light';
      setResolved(updated);
      applyTheme(updated);
    };
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, [preference]);

  return (
    <ThemeContext.Provider value={{ preference, resolved, setPreference }}>
      {children}
    </ThemeContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within a ThemeProvider');
  return ctx;
}
